import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import authServiceClient from "../services/Application/authServiceClient";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CForm,
  CFormGroup,
  CInput,
  CLabel,
  CButton,
  CRow,
  CCol,
  CContainer,
  CInputGroup,
  CInputGroupPrepend,
  CInputGroupText,
  CAlert,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";

const LoginPage = ({ onLoginSuccess }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Email must be a valid email address")
      .required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setErrorMessage(null);

      try {
        const response = await authServiceClient.signInWithEmail(values);

        if (response.succeeded) {
          onLoginSuccess(response.object);
        } else {
          setErrorMessage(
            response.errorMessage || "Login failed. Please try again.",
          );
        }
      } catch (error) {
        console.error("Login error:", error);
        setErrorMessage(
          error.message || "An unexpected error occurred. Please try again.",
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleSocialLogin = async (provider) => {
    try {
      // Use the callback URL without hash for OAuth providers
      const redirectUri = `${window.location.origin}/auth/callback`;

      // Create state parameter to pass provider info
      const state = btoa(JSON.stringify({ provider }));

      const response = await authServiceClient.getSocialLogin(
        provider,
        redirectUri,
        state,
      );

      if (response.succeeded) {
        // Redirect to the social login URL
        window.location.href = response.object.loginUrl;
      } else {
        toast.error(`Failed to initiate ${provider} login`);
      }
    } catch (error) {
      console.error(`${provider} login error:`, error);
      toast.error(`An error occurred with ${provider} login`);
    }
  };

  return (
    <div className="login-container">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md="6" lg="4">
            <CCard className="login-card p-4">
              <CCardHeader className="login-header text-center">
                <h3>Sign in to your account</h3>
                <img
                  src="/logo_eif.jpg"
                  alt="EIF Logo"
                  style={{ height: "60px", marginTop: "10px" }}
                />
              </CCardHeader>
              <CCardBody>
                {errorMessage && (
                  <CAlert color="danger" className="mb-3">
                    {errorMessage}
                  </CAlert>
                )}

                <CForm onSubmit={formik.handleSubmit} noValidate>
                  <CFormGroup>
                    <CLabel htmlFor="email">Email address</CLabel>
                    <CInputGroup>
                      <CInputGroupPrepend>
                        <CInputGroupText>
                          <CIcon name="cil-user" />
                        </CInputGroupText>
                      </CInputGroupPrepend>
                      <CInput
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Enter your email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.email && !!formik.errors.email}
                      />
                    </CInputGroup>
                    {formik.touched.email && formik.errors.email && (
                      <div className="text-danger small mt-1">
                        {formik.errors.email}
                      </div>
                    )}
                  </CFormGroup>

                  <CFormGroup>
                    <CLabel htmlFor="password">Password</CLabel>
                    <CInputGroup>
                      <CInputGroupPrepend>
                        <CInputGroupText>
                          <CIcon name="cil-lock-locked" />
                        </CInputGroupText>
                      </CInputGroupPrepend>
                      <CInput
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.password && !!formik.errors.password
                        }
                      />
                      <CInputGroupPrepend>
                        <CButton
                          type="button"
                          color="secondary"
                          variant="outline"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <img
                            src={showPassword ? "/eye-slash.png" : "/eye.png"}
                            alt={
                              showPassword ? "Hide password" : "Show password"
                            }
                            style={{ height: "14px", width: "14px" }}
                          />
                        </CButton>
                      </CInputGroupPrepend>
                    </CInputGroup>
                    {formik.touched.password && formik.errors.password && (
                      <div className="text-danger small mt-1">
                        {formik.errors.password}
                      </div>
                    )}
                  </CFormGroup>

                  <CButton
                    type="submit"
                    color="primary"
                    block
                    disabled={isSubmitting}
                    className="mb-3 mt-4"
                  >
                    {isSubmitting ? "Signing in..." : "Sign in"}
                  </CButton>
                </CForm>

                <div className="login-divider">
                  <span>Or sign in with</span>
                </div>

                <CButton
                  variant="outline"
                  block
                  className="mb-2 social-login-btn microsoft-btn"
                  onClick={() => handleSocialLogin("microsoft")}
                >
                  <img
                    src="./Microsoft_logo.svg"
                    alt="Microsoft"
                    style={{ height: "16px", marginRight: "8px" }}
                  />
                  Sign in with WTO
                </CButton>

                <CButton
                  variant="outline"
                  block
                  className="social-login-btn google-btn"
                  onClick={() => handleSocialLogin("google")}
                >
                  <img
                    src="./g-logo.png"
                    alt="Google"
                    style={{ height: "16px", marginRight: "8px" }}
                  />
                  Sign in with UNOPS
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  );
};

export default LoginPage;
